'use client';

import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { Box } from '@wishket/design-system';
import Image from 'next/image';
import { twJoin } from 'tailwind-merge';
import { use } from 'react';

import {
  SelectedSnapDisplay,
  useSelectedSnapDisplay,
} from './parts/SelectedSnapDisplay';
import { useAutoplay } from './parts/useAutoplay';

import {
  CarouselContainer,
  ContentInfoContainer,
  FadeUpContainer,
  SelectedSnapDisplayContainer,
} from './parts/Container';
import ContentInfo from './parts/ContentInfo';
import TitleChip from './parts/TitleChip';
import SlideBackgroundGradient from './parts/SlideBackgroundGradient';

import { Link } from '@/shared/Components';
import { FeaturedContentCarouselSlide } from '@/shared/types';

interface FeaturedContentCarouselProps {
  slidesPromise: Promise<FeaturedContentCarouselSlide[]>;
}

const FeaturedContentCarousel = ({
  slidesPromise,
}: FeaturedContentCarouselProps) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true }, [
    Autoplay({ delay: 4000 }),
  ]);
  const { selectedSnap, snapCount } = useSelectedSnapDisplay(emblaApi);
  const { autoplayIsPlaying, toggleAutoplay } = useAutoplay(emblaApi);

  const slides = use(slidesPromise);

  return (
    <CarouselContainer>
      <Box
        className={twJoin(
          'h-[320px] select-none overflow-hidden rounded-r-3xl rounded-bl-3xl desktop:h-[376px]'
        )}
        ref={emblaRef}
      >
        <Box className="flex h-full" data-id="featured-content-carousel">
          {slides.map((slide, index) => (
            <Link
              href={slide.detailUrl}
              className="h-full flex-[0_0_100%]"
              key={index}
            >
              <Box className="relative h-full">
                <Image
                  src={slide.src}
                  alt="carousel"
                  fill
                  className="object-cover object-center"
                  data-id={`featured-content-carousel-image-${index}`}
                  unoptimized
                />
                <SlideBackgroundGradient />
                <ContentInfoContainer>
                  <FadeUpContainer
                    enabled={index === emblaApi?.selectedScrollSnap()}
                  >
                    <ContentInfo
                      title={slide.title}
                      category={slide.category}
                      categoryUrl={slide.categoryUrl}
                    />
                  </FadeUpContainer>
                </ContentInfoContainer>
              </Box>
            </Link>
          ))}
        </Box>
        <TitleChip />
      </Box>
      <SelectedSnapDisplayContainer>
        <SelectedSnapDisplay
          selectedSnap={selectedSnap}
          snapCount={snapCount}
          autoplayIsPlaying={autoplayIsPlaying}
          onToggle={() => toggleAutoplay()}
        />
      </SelectedSnapDisplayContainer>
    </CarouselContainer>
  );
};

export default FeaturedContentCarousel;
